import { CreateCommentInput, LexicalText } from "@/schema"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import { createComment, deleteComment, toggleReaction, updateComment } from "./api"

interface CreateCommentMutationInput extends CreateCommentInput {
  commentableType: string
  commentableId: string
}

export function useCreateComment() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateCommentMutationInput) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      await createComment(context, data)
    },
    onSuccess: async (_, variables) => {
      if (variables.commentableType === "Task") {
        void queryClient.invalidateQueries({ queryKey: ["tasks"] })
        void queryClient.invalidateQueries({ queryKey: ["task", variables.commentableId] })
        void queryClient.invalidateQueries({
          queryKey: ["syncbox_email_thread"],
          predicate: ({ queryKey }) => queryKey[2] === "tasks",
        })
      }
      await queryClient.invalidateQueries({
        queryKey: ["comments", variables.commentableType, variables.commentableId],
      })
    },
  })
}
export function useUpdateComment() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, content }: { id: string; content: LexicalText }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      return updateComment(context, id, content)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comments"] })
    },
  })
}

export function useDeleteComment() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id }: { id: string; commentableType?: string; commentableId?: string }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      await deleteComment(context, id)
    },
    onSuccess: (_, variables) => {
      if (variables.commentableType === "Task") {
        void queryClient.invalidateQueries({ queryKey: ["tasks"] })
        void queryClient.invalidateQueries({ queryKey: ["task", variables.commentableId] })
        void queryClient.invalidateQueries({
          queryKey: ["syncbox_email_thread"],
          predicate: ({ queryKey }) => queryKey[2] === "tasks",
        })
      }
      // Invalidate comments query to ensure UI updates immediately
      // ActionCable will also update, but this ensures no delay
      if (variables.commentableType && variables.commentableId) {
        queryClient.invalidateQueries({
          queryKey: ["comments", variables.commentableType, variables.commentableId],
        })
      } else {
        // If commentable info not provided, invalidate all comment queries
        // This is less efficient but ensures the UI updates
        queryClient.invalidateQueries({
          queryKey: ["comments"],
        })
      }
    },
  })
}

export function useToggleReaction() {
  const getContext = useApiContext()

  return useMutation({
    mutationFn: async ({ commentId, emoji }: { commentId: string; emoji: string }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      const response = await toggleReaction(context, commentId, emoji)
      return response
    },
    // No need to invalidate - real-time updates handle this
  })
}
